/**
 * Client-side prediction + reconciliation for the local player (04 §9).
 *
 * The predictor steps the WHOLE sim locally with only the local player's
 * input, so windup/throw/dash/shield-raise and the predicted disc fall out of
 * the shared rules. On every snapshot the authoritative state replaces the
 * local copy (players, the flat disc list, match phase), acked inputs are
 * dropped, the rest are replayed through the very same `stepInPlace`. A disc
 * the server never saw thrown simply disappears on reconcile - that is the
 * rescind. Position error is folded into a visual offset that decays.
 *
 * Derez / hitstop are never predicted (A11-01): they are derived only from
 * snapshot events.
 */
import { HITSTOP_MS, INPUT_HOLD_MAX_TICKS, INPUT_RING_CAPACITY, PREDICTION_MAX_AHEAD_TICKS, RECONCILE_SNAP_DIST, TICK_DT, RECONCILE_SMOOTH_TICKS } from '../../sim/constants.js'
import { InputRing } from '../../sim/input.js'
import { cloneState, findPlayer } from '../../sim/state.js'
import { stepInPlace } from '../../sim/step.js'
import type { TickPermissions } from '../../sim/round.js'
import type { AppliedInput, PlayerInput, SimState, Vec3 } from '../../sim/types.js'
import { applySnapshot } from '../../shared/snapshot.js'
import type { SnapshotMsg } from '../../shared/protocol.js'
import type { SimEvent } from '../../shared/events.js'

export interface PredictorOptions {
  smoothingRate?: number
  snapThreshold?: number
  maxAheadTicks?: number
  /** Continue input seq numbering from a previous predictor (reconnect, room change): the server treats seq as
   *  monotonic per identity and its player keeps the last applied seq across a reattach, so a predictor that
   *  restarts at 0 has every input "acked" by that stale number before it can count as late — no lead, ever. */
  seqStart?: number
}

const HUB_HITSTOP_MS = 90

export class Predictor {
  state: SimState
  readonly pending = new InputRing<PlayerInput>(INPUT_RING_CAPACITY)
  seq = -1
  visualOffset: Vec3 = { x: 0, y: 0, z: 0 }
  lastCorrection = 0
  corrections = 0
  hardResyncs = 0
  /** Unacked inputs found stamped at or before a snapshot's tick (they reached the server late). */
  lateInputs = 0
  /** Whether the last local step was a FROZEN tick (lobby phases, REVEAL, ROUND_END, pause). */
  private lastFrozen = false
  private lateSeenUpTo = -1
  /** Wall-clock (performance.now) until which the world render is frozen. */
  hitstopUntil = 0
  /** Events from the latest snapshot (for HUD/kill feed/audio). */
  lastEvents: SimEvent[] = []
  private readonly smoothingRate: number
  private readonly snapThreshold: number
  private readonly maxAheadTicks: number

  constructor(readonly playerId: string, initial: SimState, opts: PredictorOptions = {}) {
    this.state = cloneState(initial)
    this.seq = opts.seqStart ?? -1
    this.lastFrozen = stepInPlace(cloneState(initial), new Map(), TICK_DT).frozen
    // RECONCILE_SMOOTH_TICKS: error blends out to ~5% over that many ticks.
    this.smoothingRate = opts.smoothingRate ?? 3 / (RECONCILE_SMOOTH_TICKS * TICK_DT)
    this.snapThreshold = opts.snapThreshold ?? RECONCILE_SNAP_DIST
    this.maxAheadTicks = opts.maxAheadTicks ?? PREDICTION_MAX_AHEAD_TICKS
  }

  get local() { return findPlayer(this.state, this.playerId) }
  get hitstopActive(): boolean { return performance.now() < this.hitstopUntil }

  /** One local tick. Returns the stamped input, or null when the sim was FROZEN for this tick (lobby
   *  phases, REVEAL, ROUND_END, a 10 §6 pause): the server's step returns before applying inputs, so it
   *  never acks them; queueing them here would pile up unacked inputs and trip the hard-resync bound
   *  every few snapshots (the 3–4 Hz resync storm the e2e harness caught). The tick still advances so
   *  the lead stays aligned; the decision comes from the very same `stepInPlace` the server runs. */
  apply(partial: Omit<PlayerInput, 'seq' | 'tick'>): PlayerInput | null {
    const input: PlayerInput = { ...partial, seq: this.seq + 1, tick: this.state.tick + 1 }
    this.lastFrozen = this.stepWith(input).frozen
    if (this.lastFrozen) return null
    this.seq = input.seq
    this.pending.push(input)
    return input
  }

  private stepWith(input: PlayerInput): TickPermissions {
    const inputs = new Map<string, AppliedInput>([[this.playerId, { ...input, rewindTicks: 0 }]])
    return stepInPlace(this.state, inputs, TICK_DT)
  }

  /** `serverTick`: the client's estimate of the server's CURRENT tick (net.serverTick from the pong anchor).
   *  01 §A defines PREDICTION_MAX_AHEAD_TICKS as the client leading the *server*, and the lead cap likewise
   *  bounds how far ahead of the server we stamp. The snapshot's own tick is older than the server by the
   *  downstream latency, so measuring against it is stricter than the spec by exactly that age — enough,
   *  under 80 ms / 20 ms jitter / 1 % loss, to read every delayed snapshot as a runaway. Defaults to the
   *  snapshot tick (no estimate available). */
  reconcile(snap: SnapshotMsg, now: number = performance.now(), serverTick: number = snap.tick): void {
    this.lastEvents = snap.events
    for (const e of snap.events) {
      if (e.type === 'derez') {
        const ms = this.state.mode === 'hub' ? HUB_HITSTOP_MS : HITSTOP_MS
        this.hitstopUntil = Math.max(this.hitstopUntil, now + ms)
      }
    }
    const before = this.local ? { x: this.local.pos.x, z: this.local.pos.z } : null
    const ahead = this.state.tick - snap.tick
    const leadOverServer = this.state.tick - Math.max(snap.tick, serverTick)
    applySnapshot(this.state, snap)
    const me = this.local
    if (!me) return
    // Only running AWAY from the authority is a hard resync (prediction discarded). Being BEHIND it
    // (`ahead < 0`: a cold start from a stale welcome state, an un-freeze with no lead yet, a jitter
    // burst) is a lead deficit: every unacked input is now late, and the late-input hold below steps
    // us forward by up to INPUT_HOLD_MAX_TICKS per snapshot until the stamps arrive on time again.
    if (leadOverServer > this.maxAheadTicks) {
      this.pending.clear()
      this.visualOffset = { x: 0, y: 0, z: 0 }
      // On a frozen tick there is no prediction to protect: a burst simply lands us on the newer snapshot.
      if (!this.lastFrozen) this.hardResyncs++
      return
    }
    this.pending.ackUpTo(me.lastInputSeq)
    // Replay by TICK, mirroring the server's schedule (Session.takeDue). The snapshot at tick N is the
    // authority through N. An unacked input stamped <= N had not reached the server by N: it will be
    // rewound in, and the server stood in for it with INPUT_HOLD (previous levels), so re-stepping it here
    // would double-count it. Worse, counting by seq lands state.tick off by one, the next stamp collides
    // with or skips a tick, the server holds again, and the client oscillates by a tick of movement on
    // every snapshot from then on (the live rubber-band). So: step only inputs stamped after N, in stamp
    // order; then, for each late input, step one HELD tick (same levels, edges cleared) exactly as the
    // server did, which also moves our stamps ahead so the next inputs arrive on time.
    // Each late input counts once (`lateSeenUpTo`): a lost record stays unacked until the server passes
    // it, and re-counting it every snapshot would run the lead away.
    const unacked = this.pending.since(me.lastInputSeq)
    const hold = (of: PlayerInput): PlayerInput => ({ ...of, throw: false, dash: false, jump: false, tick: this.state.tick + 1 })
    let late = 0
    let last: PlayerInput | null = null
    for (const input of unacked) {
      if (input.tick <= this.state.tick) { if (input.seq > this.lateSeenUpTo) { late++; this.lateSeenUpTo = input.seq }; continue }
      // A gap in our stamps (left by an earlier lead advance) is a tick the server held: hold it here too, so
      // state.tick tracks the stamps exactly and the next stamp never collides with one already sent.
      for (let g = 0; g < INPUT_HOLD_MAX_TICKS && this.state.tick + 1 < input.tick; g++) this.stepWith(hold(last ?? input))
      this.stepWith(input)
      last = input
    }
    const held = last ?? unacked[unacked.length - 1] ?? null
    // The lead advance stops at the bound the next reconcile enforces: overrunning it would be a
    // self-inflicted hard resync. At the cap the client simply stays a tick late and the server's
    // INPUT_HOLD stands in, which is the designed behaviour for an RTT this bound cannot cover.
    const room = this.maxAheadTicks - (this.state.tick - Math.max(snap.tick, serverTick))
    if (held) for (let i = 0; i < Math.min(late, INPUT_HOLD_MAX_TICKS, Math.max(0, room)); i++) this.stepWith(hold(held))
    this.lateInputs += late
    // Frozen tick, nothing queued: keep the lead we had (empty ticks; the frozen sim ignores them) so the
    // next snapshot burst cannot put us behind the authority and the un-freeze starts on time.
    if (this.lastFrozen && unacked.length === 0) for (let i = 0; i < Math.max(0, ahead); i++) stepInPlace(this.state, new Map(), TICK_DT)
    if (before) {
      const after = this.local!.pos
      const ex = before.x - after.x
      const ez = before.z - after.z
      const mag = Math.hypot(ex, ez)
      this.lastCorrection = mag
      if (mag > 1e-4) {
        this.corrections++
        if (mag < this.snapThreshold) { this.visualOffset.x += ex; this.visualOffset.z += ez }
        else { this.visualOffset.x = this.visualOffset.z = 0 }
      }
    }
  }

  resync(state: SimState): void {
    this.state = cloneState(state)
    this.lastFrozen = stepInPlace(cloneState(state), new Map(), TICK_DT).frozen
    this.pending.clear()
    this.visualOffset = { x: 0, y: 0, z: 0 }
  }

  smooth(dtSec: number): void {
    const k = Math.exp(-this.smoothingRate * dtSec)
    this.visualOffset.x *= k
    this.visualOffset.z *= k
    if (Math.abs(this.visualOffset.x) < 1e-3) this.visualOffset.x = 0
    if (Math.abs(this.visualOffset.z) < 1e-3) this.visualOffset.z = 0
  }

  renderPos(): Vec3 | null {
    const p = this.local
    if (!p) return null
    return { x: p.pos.x + this.visualOffset.x, y: p.pos.y, z: p.pos.z + this.visualOffset.z }
  }
}
